import { useRecoilState, useRecoilValue } from 'recoil'
import clsx from 'clsx'
import Decimal from 'decimal.js'
import { markPriceMapState, marketInfoValue } from '@/recoil/Market'
import {
  positionInfoState,
  positionDetailValue,
  collateralInfoValue,
  liquidationPriceMapValue
} from '@/recoil/Position'
import { maxOrderSizeValue } from '@/recoil/OrderForm'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import { ORDER_SIDE } from '@/constants/order'
import {
  calculationOrderLiquidationPrice,
  calculateNewLeverage
} from '@/utils/futures'
import './RiskCalculator.css'

function formatDisplayData(n) {
  return Decimal(n).eq(0) || Decimal(n).isNaN() || Decimal(n).lt(0) ? '--' : n
}

export default function RiskCalculator({
  id,
  orderSide,
  orderPrice,
  orderSize
}) {
  const { accountId } = useWalletSelector()
  const [markPriceMap] = useRecoilState(markPriceMapState)
  const [positionInfo] = useRecoilState(positionInfoState)
  const marketInfo = useRecoilValue(marketInfoValue)
  const { totalMaintenanceMargin, maintenanceMarginMap } =
    useRecoilValue(positionDetailValue)
  const { totalCollateral } = useRecoilValue(collateralInfoValue)
  const liquidationPriceMap = useRecoilValue(liquidationPriceMapValue)
  const maxOrderSize = useRecoilValue(maxOrderSizeValue(id))

  const positionList = positionInfo.rows || []
  const { position_qty: positionQty = 0 } =
    positionList.find(({ symbol }) => symbol === marketInfo.symbol) || {}
  const markPrice = markPriceMap[marketInfo.symbol] || 0
  const maintenanceMargin = maintenanceMarginMap[marketInfo.symbol] || 0

  const accountLeverage = accountId
    ? calculateNewLeverage({
        orderSide,
        orderSize: orderSize || 0,
        positionQty,
        markPrice,
        totalCollateral
      })
    : 0

  const isReverseSideOrder =
    (orderSide === ORDER_SIDE.sell && Decimal(positionQty).gt(0)) ||
    (orderSide === ORDER_SIDE.buy && Decimal(positionQty).lt(0))
  const liquidationPrice = Decimal(maxOrderSize).eq(0)
    ? liquidationPriceMap[marketInfo.symbol] || 0
    : isReverseSideOrder &&
      Decimal(maxOrderSize).sub(Decimal(positionQty).abs()).eq(0)
    ? 0
    : calculationOrderLiquidationPrice({
        positionQty,
        orderSide,
        orderPrice: orderPrice || 0,
        orderSize: orderSize || 0,
        markPrice,
        maintenanceMargin,
        totalMaintenanceMargin,
        totalCollateral
      })

  // const liquidationPriceRoundMode = Decimal(positionQty).gt(0)
  //   ? Decimal.ROUND_FLOOR
  //   : Decimal.ROUND_CEIL

  return (
    <div className="risk-calculator">
      <div className="info-row">
        <div className="info-label">Max</div>
        {accountId && (
          <div
            className={clsx({
              'color-positive': orderSide === ORDER_SIDE.buy,
              'color-negative': orderSide === ORDER_SIDE.sell
            })}
          >
            {Decimal(maxOrderSize).toFixed(
              marketInfo.sizeDecimal,
              Decimal.ROUND_DOWN
            )}
          </div>
        )}
      </div>
      <div className="info-row">
        <div className="info-label">Acc. Leverage</div>
        {accountId && (
          <div>
            {formatDisplayData(
              Decimal(accountLeverage).toFixed(2, Decimal.ROUND_CEIL)
            )}
            x
          </div>
        )}
      </div>
      <div className="info-row">
        <div className="info-label">Liq. Price</div>
        {accountId && (
          <div>
            {formatDisplayData(
              Decimal(liquidationPrice).toFixed(
                marketInfo.priceDecimal,
                Decimal.ROUND_DOWN
              )
            )}
          </div>
        )}
      </div>
    </div>
  )
}
